import React, { Fragment, useEffect, useState } from "react";
import { getCreditLockStatus, updateCreditStatusLock, updateCreditStatusUnlock } from "../../../store/service";
import TransunionLogo from "../../../Assets/images/logo_transunion.png";
import CreditAccordion from "../CreditAccordion";
import { useTranslation } from "react-i18next";
import { Notification } from "../../common/Notification";
import ApiFailedError from "../../MobileAttackView/ApiFailedError";
import CreditFreezeDialog from "../../Dialogs/CreditFreezeDialog";
import { DialogComponent } from "../../common/DialogComponent";

const lockDescriptionList = [
    "Credit applications using your name are stopped.",
    "Get alerts if someone tries to make a hard inquiry on your credit report.",
    "Potential landlords and employers can still access your report.",
    "Instantly unlock when you want to apply for credit.",
];

const unLockDescriptionList = [
    "Lenders can pull your report if you apply for loans or credit.",
    "You could be at higher risk for fraud and identity theft.",
    "Instantly lock your report when not applying for credit.",
];

function CreditLock() {
    const { t: tLang } = useTranslation();
    const [status, setStatus] = useState("");
    const [isFreeze, setIsFreeze] = useState(false);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        getCreditLockStatus()
            .then((res) => {
                setLoading(false);
                const {
                    getStatusResponse: { actionData, errorResponse },
                    hasError,
                } = res;
                if (!hasError) {
                    if (actionData && actionData.statusCode) {
                        setStatus(actionData.statusCode);
                        if (actionData.statusCode === "FREEZE") {
                            setIsFreeze(true);
                        }
                    }
                } else {
                    Notification({
                        type: "error",
                        title: "Credit Lock Status",
                        message: errorResponse ? errorResponse.message : "",
                        duration: 5,
                    });
                    setTimeout(() => {
                        var elements = document.getElementsByClassName("ant-notification-notice-close");
                        if (elements.length > 0) {
                            var firstElement = elements[0];
                            firstElement.setAttribute("role", "button");
                        }
                    }, 1000);
                }
            })
            .catch((err) => {
                setLoading(false);
                Notification({
                    type: "error",
                    title: "Credit Lock Status",
                    message: err.errorMessage,
                    duration: 5,
                });
                setTimeout(() => {
                    var elements = document.getElementsByClassName("ant-notification-notice-close");
                    if (elements.length > 0) {
                        var firstElement = elements[0];
                        firstElement.setAttribute("role", "button");
                    }
                }, 1000);
            });
    }, []);

    const changeStatus = () => {
        if (status === "LOCKED") {
            setLoading(true);
            updateCreditStatusUnlock()
                .then((res) => {
                    setLoading(false);
                    if (!res.hasError) {
                        setStatus("UN_LOCKED");
                    } else {
                        Notification({
                            type: "error",
                            title: "Credit Lock Status",
                            message: "We were unable to unlock your credit report. Please try again.",
                            duration: 5,
                        });
                    }
                })
                .catch((err) => {
                    setLoading(false);
                    Notification({
                        type: "error",
                        title: "Credit Lock Status",
                        message: err.errorMessage,
                        duration: 5,
                    });
                });
        } else if (status === "UN_LOCKED" || status === "NO_LOCK_OR_FREEZE") {
            setLoading(true);
            updateCreditStatusLock()
                .then((res) => {
                    setLoading(false);
                    if (!res.hasError) {
                        setStatus("LOCKED");
                    } else {
                        Notification({
                            type: "error",
                            title: "Credit Lock Status",
                            message: "We were unable to lock your credit report. Please try again.",
                            duration: 5,
                        });
                    }
                })
                .catch((err) => {
                    setLoading(false);
                    Notification({
                        type: "error",
                        title: "Credit Lock Status",
                        message: err.errorMessage,
                        duration: 5,
                    });
                });
        } else {
            setIsFreeze(true);
        }
    };

    const listDescription = (desc: string[]) => {
        return (
            <ul className="credit-lock-description-ul">
                {desc.map((el, i) => (
                    <li key={i}>
                        <p>{el}</p>
                    </li>
                ))}
            </ul>
        );
    };

    const creditLockInfo = (index: number) => {
        switch (index) {
            case 1:
                return (
                    <div className="credit-lock-collapse-ul">
                        <div className="row">
                            <div className="col-xs-12">
                                <ul className="credit-lock-description-ul">
                                    <li>
                                        Credit Lock gives you the ability to lock and unlock access to your TransUnion credit report at the touch of a button—giving
                                        you more control over your credit health.
                                    </li>
                                    <li>When your credit report is locked, lenders won't be able to pull your file when you apply for new accounts.</li>
                                    <li>You'll get an alert if someone tries to make a hard inquiry on your credit report while it's locked.</li>
                                    <li>
                                        Locking your credit report blocks others from looking at it, which can help stop an identity thief from opening credit in
                                        your name.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="credit-lock-collapse-ul">
                        <div className="row">
                            <div className="col-xs-12">
                                <ul className="credit-lock-description-ul">
                                    <li>
                                        Credit Lock and credit freeze (also known as a security freeze) both block access to your credit report, stopping new
                                        accounts from being opened in your name.
                                    </li>
                                    <li>
                                        Credit Lock alerts you when someone applies for credit in your name. A credit freeze does not send you alerts, but you can
                                        log in to the TransUnion Service Center at any time to see your freeze status.
                                    </li>
                                    <li>
                                        A fraud alert tells lenders to verify your identity before extending new credit, but it doesn't block access to your credit
                                        report.
                                    </li>
                                    <li>
                                        You can learn more about credit freezes at
                                        <a
                                            className="default_link link"
                                            href="https://www.TransUnion.com/credit-freeze"
                                            id="btn-leave-feedback"
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {" "}
                                            TransUnion.com/credit-freeze
                                        </a>
                                        and fraud alerts at
                                        <a
                                            className="default_link link"
                                            href="https://www.Transunion.com/fraud-alerts"
                                            id="btn-leave-feedback"
                                            target="_blank"
                                            rel="noreferrer"
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {" "}
                                            Transunion.com/fraud-alerts
                                        </a>
                                        .
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="credit-lock-collapse-text">
                        <div className="row">
                            <div style={{ paddingTop: "0.5rem", paddingLeft: "0.5rem" }}>
                                <p>
                                    You'll receive an inquiry alert when someone tries to access your credit report while it's locked. You'll also get email
                                    confirmations when you lock and unlock your credit.
                                </p>
                            </div>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className="credit-lock-collapse-text">
                        <div className="row">
                            <div style={{ paddingTop: "0.5rem", paddingLeft: "0.5rem" }}>
                                <p>
                                    If someone tries to apply for credit in your name while your credit report is locked, access will be denied, and we'll notify
                                    you about it.
                                </p>
                            </div>
                        </div>
                    </div>
                );
            case 5:
                return (
                    <div className="credit-lock-collapse-text">
                        <div className="row">
                            <div style={{ paddingTop: "0.5rem", paddingLeft: "0.5rem" }}>
                                <p>
                                    Yes. When your TransUnion credit report is locked, a creditor can still open an account using your file from one of the other
                                    major credit bureaus where it isn't locked or frozen. This could result in new information being added to your TransUnion credit
                                    report.
                                </p>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const creditLockHeader = [
        {
            id: 1,
            name: "What is Credit Lock?",
            content: creditLockInfo(1),
        },
        {
            id: 2,
            name: "What's the difference between Credit Lock, Credit Freeze and a Fraud Alert?",
            content: creditLockInfo(2),
        },
        {
            id: 3,
            name: "What kind of alerts will I receive when my credit is locked?",
            content: creditLockInfo(3),
        },
        {
            id: 4,
            name: "How can locking my credit help prevent identity theft and fraud?",
            content: creditLockInfo(4),
        },
        {
            id: 5,
            name: "Can information be added to my credit file while locked?",
            content: creditLockInfo(5),
        },
    ];

    const closeFreezeDialog = () => {
        setIsFreeze(false);
    };
    return (
        <div className={loading ? "row loading" : "row"} id="content">
            {status ? (
                <div className="col-xs-12 col-sm-8">
                    <div className="box">
                        <div className="box-header">
                            <div className="box-title-container padding-left-3">
                                <div className="row">
                                    <div className="col-xs-12">
                                        <div className="box-title-icon" aria-hidden="true">
                                            <em className="las la-lock"></em>
                                        </div>
                                        <div className="box-title-text">
                                            <h2 className="box-title">{tLang("credit.tabs.creditLock")}</h2>
                                        </div>
                                    </div>
                                    <div className="col-xs-12">
                                        <div className="row flex-center justify-space-bt">
                                            <img src={TransunionLogo} alt="TransUnion Logo" className="tu-logo" />
                                            <div className="credit-lock-status-main">
                                                <label>Status:</label>
                                                <div className={`${status === "LOCKED" ? "btn-credit-lock-success" : "btn-credit-unlock"}`} onClick={changeStatus}>
                                                    {status !== "LOCKED" && (
                                                        <span className="btn-icon" style={{ margin: "1px" }}>
                                                            <em className="las la-lock-open"></em>
                                                        </span>
                                                    )}
                                                    <button className={`btn-default ${status === "LOCKED" ? "btn-locked-text" : "btn-unlocked-text"}`}>
                                                        <p>{status === "LOCKED" ? "Locked" : "Unlocked"}</p>
                                                    </button>
                                                    {status === "LOCKED" && (
                                                        <span className="btn-icon" style={{ margin: "2px 1px 1px 1px" }}>
                                                            <em className="las la-lock"></em>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="box">
                        <div className="box-content">
                            <CreditAccordion
                                headers={creditLockHeader.map((v: any, i: number) => (
                                    <Fragment key={`${v.id}-${i}`}>{v.name}</Fragment>
                                ))}
                                content={creditLockHeader.map((v: any, i: number) => (
                                    <Fragment key={v.id}>{v.content}</Fragment>
                                ))}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
            {status ? (
                <div className="col-xs-12 col-sm-4">
                    <div className="box box-border-top">
                        <div className="box-header">
                            <div className="box-title-container">
                                <div className="box-title-text">
                                    <h4 className="box-title">{status === "LOCKED" ? "While your credit is locked:" : "While your credit is unlocked:"}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="box-content">{listDescription(status === "LOCKED" ? lockDescriptionList : unLockDescriptionList)}</div>
                    </div>
                    <div className="box box-border-top">
                        <div className="box-header">
                            <div className="box-title-container">
                                <div className="box-title-text">
                                    <h4 className="box-title">{status === "LOCKED" ? "Unlock your credit when:" : "Lock your credit when:"}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="box-content">
                            <p>
                                {status === "LOCKED"
                                    ? `You apply for new credit cards or loans, since lenders will likely check your credit report before making a decision.`
                                    : `You're not actively applying for new credit cards or loans, or after a lender checks your application. That way, a potential identity thief can't apply for credit in your name.`}
                            </p>
                        </div>
                    </div>
                </div>
            ) : !loading ? (
                <div className="col-xs">
                    <div className="box">
                        <ApiFailedError fromApiError fromRegistration={false} />
                    </div>
                </div>
            ) : null}
            <div className="credit-freeze-dialog">
                {isFreeze && (
                    <DialogComponent dialogId="dialog-credit-freeze" title="Credit Freeze Dialog" closeDialog={closeFreezeDialog}>
                        <CreditFreezeDialog closeDialog={closeFreezeDialog} />
                    </DialogComponent>
                )}
            </div>
        </div>
    );
}

export default CreditLock;
